import { Info } from "@mui/icons-material";
import classNames from "classnames";
import React, { useEffect } from "react";
import { Button } from "~/components/ui/core/Button";
import { LegacyHeading } from "~/components/ui/core/LegacyHeading";
import { Link } from "~/components/ui/core/Link";
import { PercentilesSlider } from "~/components/ui/PercentilesSlider";
import { usePermissions } from "~/hooks/usePermissions";
import { getConsistentPercentilesValue, type PercentileBounds } from "~/services/percentiles";

type Event = {
  target: {
    name?: string;
    value: PercentileBounds;
  };
};

type Props = {
  name?: string;
  className?: string;
  value: PercentileBounds;
  disabled?: boolean;
  readOnly?: boolean;
  onChange: (event: Event) => void;
};

export const PercentilesSelector: React.FC<Props> = ({
  className,
  value,
  disabled = false,
  readOnly = false,
  onChange,
}) => {
  const { permissions } = usePermissions();

  useEffect(() => {
    const consistentPercentilesValue = getConsistentPercentilesValue(value);
    if (consistentPercentilesValue) {
      onChange({ target: { value: consistentPercentilesValue } });
    }
  }, [onChange, value]);

  return (
    <div
      className={classNames(className, {
        "flex flex-col space-y-2 pb-2": true,
      })}
    >
      <div className="flex flex-col space-y-2">
        <div className="flex">
          <LegacyHeading size="lg" className="normal-case">
            Percentiles
          </LegacyHeading>

          {readOnly && (
            <LegacyHeading className="ml-2" icon="lock" size="md">
              Read only mode
            </LegacyHeading>
          )}
        </div>

        <span className="whitespace-pre-wrap text-xs text-gray-600">
          Configure percentiles boundaries to fit your company's compensation guidelines.
        </span>
      </div>

      <Button
        component={Link}
        to="https://help.figures.hr/en/articles/6201775-statistics-101"
        variant="outlined"
        size="small"
        startIcon={<Info />}
      >
        What are percentiles?
      </Button>

      <div className="-mx-2 flex h-20 px-2 pt-8">
        <PercentilesSlider value={value} onChange={onChange} disabled={disabled || readOnly} />
      </div>

      <div className="flex flex-col space-y-0.5 text-xs text-gray-600">
        <span>
          · This range includes <span className="font-semibold">{Math.round((value.high - value.low) * 100)}%</span> of
          the total sample size.
        </span>
        <span>
          · The lower <span className="font-semibold">{Math.round(value.low * 100)}%</span> of the sample is excluded.
        </span>
        <span>
          · The upper <span className="font-semibold">{Math.round(value.high * 100)}%</span> of the sample is excluded.
        </span>
        <span>
          · Displayed values match the{" "}
          <span className="font-semibold">{Math.round(value.middle * 100)}th percentile</span> of the sample.
        </span>
      </div>

      {permissions.canAccessSettings && (
        <div className="mt-2 flex items-center border-t pt-4 pb-2">
          <span className="ml-2 text-xs">
            You can configure percentiles in your{" "}
            <Link to="/account/compensation-policy" newTab>
              account settings
            </Link>
            .
          </span>
        </div>
      )}
    </div>
  );
};
